import { useState, useEffect } from 'react';
import { useMentiSession } from '../../../context/MentiSessionContext';
import { getPresentation } from '../../../services/mentiService';
import { submitResponse } from '../../../services/mentiParticipantService';
import WordCloudInput from './WordCloudInput';
import MultipleChoiceInput from './MultipleChoiceInput';
import OpenEndedInput from './OpenEndedInput';
import ScaleInput from './ScaleInput';
import ContentSlide from './ContentSlide';

/**
 * Main participant view for Mikkel Menti.
 * Listens to session state and renders the appropriate input for each slide.
 *
 * @param {Object} props
 * @param {string} props.sessionId
 * @param {string} props.participantId
 * @param {function} props.onLeave
 */
export default function MentiResponseView({ sessionId, participantId, onLeave }) {
  const { session, loading: sessionLoading, error: sessionError } = useMentiSession();
  const [presentation, setPresentation] = useState(null);
  const [loadingPresentation, setLoadingPresentation] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Fetch presentation once we have the session
  useEffect(() => {
    if (!session?.presentationId) return;

    let cancelled = false;
    const fetchPresentation = async () => {
      try {
        const pres = await getPresentation(session.presentationId);
        if (!cancelled) {
          setPresentation(pres);
        }
      } catch (err) {
        console.error('Failed to fetch presentation:', err);
      } finally {
        if (!cancelled) {
          setLoadingPresentation(false);
        }
      }
    };

    fetchPresentation();
    return () => { cancelled = true; };
  }, [session?.presentationId]);

  const handleSubmit = async (value) => {
    if (!currentSlide || submitting) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      await submitResponse(sessionId, participantId, currentSlide.id, value);
    } catch (err) {
      setSubmitError(err.message || 'Klarte ikkje sende svaret');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading states
  if (sessionLoading || loadingPresentation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/80 text-lg">Lastar inn...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (sessionError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center p-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center max-w-md border border-white/15">
          <span className="material-symbols-outlined text-red-300 text-5xl mb-4">error</span>
          <h2 className="text-white text-xl font-bold mb-2">Noko gjekk gale</h2>
          <p className="text-white/70 mb-6">{sessionError}</p>
          <button
            onClick={onLeave}
            className="px-6 py-3 bg-white/20 text-white rounded-full font-medium hover:bg-white/30 transition-colors"
          >
            Gå tilbake
          </button>
        </div>
      </div>
    );
  }

  // Waiting for session to start
  if (!session || session.status === 'waiting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center p-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center max-w-md border border-white/15">
          <span className="material-symbols-outlined text-blue-200 text-5xl mb-4 animate-pulse">hourglass_top</span>
          <h2 className="text-white text-2xl font-bold mb-2">Ventar på at presentasjonen startar...</h2>
          <p className="text-white/60 mt-4">Du er kopla til. Presentasjonen startar snart.</p>
        </div>
      </div>
    );
  }

  // Session finished
  if (session.status === 'finished') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center p-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center max-w-md border border-white/15">
          <span className="material-symbols-outlined text-blue-200 text-5xl mb-4">flag</span>
          <h2 className="text-white text-2xl font-bold mb-2">Presentasjonen er avslutta</h2>
          <p className="text-white/60 mb-6">Takk for at du var med!</p>
          <button
            onClick={onLeave}
            className="px-6 py-3 bg-white/20 text-white rounded-full font-medium hover:bg-white/30 transition-colors"
          >
            Gå tilbake
          </button>
        </div>
      </div>
    );
  }

  // Determine current slide
  const slides = presentation?.slides || [];
  const currentSlideIndex = session.currentSlideIndex ?? 0;
  const currentSlide = slides[currentSlideIndex];

  if (!currentSlide) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center p-6">
        <div className="text-center">
          <span className="material-symbols-outlined text-white/50 text-5xl mb-4">hourglass_top</span>
          <p className="text-white/70 text-lg">Ventar på neste lysbilde...</p>
        </div>
      </div>
    );
  }

  // Check if already responded
  const hasResponded = !!session.responses?.[currentSlide.id]?.[participantId];

  // Render input component based on slide type
  const renderInput = () => {
    if (currentSlide.type === 'content') {
      return <ContentSlide slide={currentSlide} />;
    }

    if (hasResponded) {
      return (
        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center border border-white/15">
          <span className="material-symbols-outlined text-green-300 text-6xl mb-4">check_circle</span>
          <h2 className="text-white text-2xl font-bold mb-2">Takk for svaret!</h2>
          <p className="text-white/60">Ventar på neste lysbilde...</p>
        </div>
      );
    }

    const inputProps = {
      slide: currentSlide,
      onSubmit: handleSubmit,
      loading: submitting
    };

    switch (currentSlide.type) {
      case 'wordcloud':
        return <WordCloudInput {...inputProps} />;
      case 'multiple_choice':
        return <MultipleChoiceInput {...inputProps} />;
      case 'open_ended':
        return <OpenEndedInput {...inputProps} />;
      case 'scale':
        return <ScaleInput {...inputProps} />;
      default:
        return (
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center border border-white/15">
            <p className="text-white/70">Ukjend lysbiletype: {currentSlide.type}</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-700 flex flex-col">
      {/* Top bar */}
      <div className="bg-blue-800/40 backdrop-blur-sm px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <span className="material-symbols-outlined text-white/70 text-xl">slideshow</span>
          <span className="text-white font-medium truncate text-sm">
            {presentation?.title || 'Menti'}
          </span>
        </div>
        <span className="text-white/50 text-sm shrink-0">
          {currentSlideIndex + 1} / {slides.length}
        </span>
      </div>

      {/* Slide title */}
      {currentSlide.title && currentSlide.type !== 'content' && (
        <div className="px-6 pt-6 pb-2">
          <h2 className="text-white text-xl md:text-2xl font-bold text-center">
            {currentSlide.title}
          </h2>
          {currentSlide.description && (
            <p className="text-white/60 text-center mt-1">{currentSlide.description}</p>
          )}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          {renderInput()}

          {submitError && (
            <div className="mt-4 text-white bg-red-500/25 border border-red-400/30 px-4 py-3 rounded-xl text-center font-medium">
              {submitError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
